import { Box, Button, Flex, Image } from '@modusign/design-system';
import { MouseEvent as ReactMouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import uploadIcon from '@app/assets/icons/icon-upload.svg';
import {
  WindowModal,
  WindowModalContent,
  WindowModalFooter,
  WindowModalHeader,
} from '@app/components/Modal/WindowModal';
import { ACCEPT_FILETYPE, MAX_FILE_UPLOAD_BYTE } from '@app/constants/UpLoadModal';
import { recordGTMEvent } from '@app/store/analytics/actions';
import SampleFile from '@app/store/documents/SampleFile';
import UploadFile from '@app/store/documents/UploadFile';
import { modalOpen } from '@app/store/modal/actions';

import SideBarMenuType from './SideBarMenuType';
import UpLoadModalContent from './UpLoadModalContent';
import UpLoadModalSidebar from './UpLoadModalSideBar';
import UploadType from './UploadType';

interface UploadModalProps {
  initialSideBarMenu: SideBarMenuType;
  isLoading?: boolean;
  isOpen: boolean;
  needDraft: boolean;
  needTemplate: boolean;
  onClose: () => void;
  onSubmit: (file: File | UploadFile | SampleFile) => void;
}

const defaultProps = {
  initialSideBarMenu: 'LOCAL',
  needDraft: false,
  needTemplate: false,
};

function UploadModal({
  initialSideBarMenu,
  isLoading,
  isOpen,
  needDraft,
  needTemplate,
  onClose,
  onSubmit,
}: UploadModalProps) {
  const dispatch = useDispatch();
  const history = useHistory();

  const [file, setFile] = useState<File | UploadFile | SampleFile | null>(null);
  const [sideBarMenu, setSideBarMenu] = useState<SideBarMenuType>(initialSideBarMenu);

  const initialUploadType = useMemo(() => {
    switch (initialSideBarMenu) {
      case 'CLOUD_DRIVE':
        return 'GOOGLE_DRIVE';
      case 'SAMPLE_DOCUMENTS':
        return 'SAMPLE_DOCUMENTS';
      default:
        return 'LOCAL';
    }
  }, [initialSideBarMenu]);

  const [uploadType, setUploadType] = useState<UploadType>(initialUploadType);

  const handleReset = useCallback(() => {
    setFile(null);
  }, []);

  useEffect(() => {
    if (isOpen) {
      handleReset();
      setSideBarMenu(initialSideBarMenu);
      setUploadType(initialUploadType);
    }
  }, [handleReset, initialSideBarMenu, initialUploadType, isOpen]);

  const handleSelect = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
      const { name } = event.currentTarget;

      if (name === 'DRAFT') {
        onClose();
        history.push('/drafts');
        dispatch(
          recordGTMEvent('appDraftsClick', {
            clickOn: '문서업로드모달',
          })
        );
      }
      if (name === 'TEMPLATE') {
        onClose();
        history.push('/templates');
      }

      if (name === 'LOCAL') {
        setUploadType('LOCAL');
        setSideBarMenu('LOCAL');
      } else if (name === 'SAMPLE_DOCUMENTS') {
        setUploadType('SAMPLE_DOCUMENTS');
        setSideBarMenu('SAMPLE_DOCUMENTS');
      } else if (name === 'CLOUD_DRIVE') {
        setUploadType('GOOGLE_DRIVE');
        setSideBarMenu('CLOUD_DRIVE');
      }
      handleReset();
    },
    [dispatch, handleReset, history, onClose]
  );

  const handleOnDrop = useCallback(
    (acceptFile: Array<File | UploadFile>) => {
      if (!acceptFile[0]) {
        dispatch(
          modalOpen('CONFIRM', {
            content: '파일이 선택되지 않았습니다.',
            closeText: '확인',
          })
        );
        return;
      }
      const parsing = acceptFile[0].name.split('.');
      const extension = parsing.pop()?.toLowerCase();

      if (!extension || !ACCEPT_FILETYPE.includes(extension)) {
        dispatch(
          modalOpen('CONFIRM', {
            title: '지원하지 않는 파일 형식',
            content: '해당 문서의 파일 형식은 지원되지 않습니다.',
            closeText: '확인',
          })
        );
        return;
      }
      if (acceptFile[0].size > MAX_FILE_UPLOAD_BYTE) {
        dispatch(
          modalOpen('CONFIRM', {
            title: '업로드 용량 초과',
            content: '최대 업로드 가능 용량은 5MB입니다.',
            closeText: '확인',
          })
        );
        return;
      }
      setFile(acceptFile[0]);
    },
    [dispatch]
  );

  const handleOnSampleDocumentSelect = useCallback((documentUrl, documentName) => {
    setFile({ name: `${documentName}.pdf`, url: documentUrl });
  }, []);

  function handleSubmit() {
    if (file) {
      onSubmit(file);
    }
  }

  return (
    <WindowModal
      isOpen={isOpen}
      maxWidth="md"
      testId="documentUploadModal"
      onRequestClose={onClose}
    >
      <WindowModalHeader sx={{ color: 'textNeutralLight' }} onClose={onClose}>
        <Flex alignItems="center">
          <Image alt="upload-icon" src={uploadIcon} sx={{ mr: 2 }} />
          문서 업로드
        </Flex>
      </WindowModalHeader>
      <WindowModalContent px={0} py={0}>
        <Flex>
          <UpLoadModalSidebar
            needDraft={needDraft}
            needTemplate={needTemplate}
            sideBarMenuType={sideBarMenu}
            onSelect={handleSelect}
          />
          <UpLoadModalContent
            fileName={file?.name}
            uploadType={uploadType}
            onDrop={handleOnDrop}
            onReset={handleReset}
            onSampleDocumentSelect={handleOnSampleDocumentSelect}
            onSelect={handleSelect}
          />
        </Flex>
      </WindowModalContent>
      <WindowModalFooter>
        <Box textAlign="right">
          <Button
            disabled={!file}
            isLoading={isLoading}
            variant="primary"
            width="wide"
            onClick={handleSubmit}
          >
            업로드
          </Button>
        </Box>
      </WindowModalFooter>
    </WindowModal>
  );
}

UploadModal.defaultProps = defaultProps;

export default UploadModal;
